'use client';

import { CheckCircle2, Plus, RotateCcw, Sparkles, Target, Trash2 } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

type TaskItem = {
  id: string;
  title: string;
  text: string;
  done: boolean;
};

const defaultTasks: Omit<TaskItem, 'id' | 'done'>[] = [
  {
    title: 'Проверить расписание и дорогу',
    text: 'Понять, где пары, сколько идти до корпуса и какой транспорт удобнее.',
  },
  {
    title: 'Собрать документы и сроки',
    text: 'Сохранить справки, заявления, дедлайны и важные контакты в одном месте.',
  },
  {
    title: 'Разделить бюджет на неделю',
    text: 'Отложить деньги на еду, транспорт, связь и небольшой запас.',
  },
];

export default function GuidesProgressChecklist() {
  const pathname = usePathname();
  const { data: session, status } = useSession();
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [newText, setNewText] = useState('');

  const isAuthed = Boolean(session?.user?.id);

  useEffect(() => {
    if (!isAuthed) {
      setIsLoading(false);
      return;
    }

    const load = async () => {
      setIsLoading(true);
      const response = await fetch('/api/students/guides/checklist', { cache: 'no-store' });
      const data = await response.json();
      if (response.ok && Array.isArray(data.items) && data.items.length) {
        setTasks(data.items);
      } else {
        setTasks(
          defaultTasks.map((task, i) => ({
            ...task,
            id: `draft-${i}`,
            done: false,
          })),
        );
      }
      setIsLoading(false);
    };

    load();
  }, [isAuthed]);

  const completedCount = useMemo(() => tasks.filter((task) => task.done).length, [tasks]);
  const progressPercent = tasks.length ? Math.round((completedCount / tasks.length) * 100) : 0;

  async function createTask() {
    if (!newTitle.trim() || !newText.trim()) return;

    const response = await fetch('/api/students/guides/checklist', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ title: newTitle, text: newText }),
    });

    const data = await response.json();
    if (response.ok && data.item) {
      setTasks((prev) => [...prev.filter((t) => !t.id.startsWith('draft-')), data.item]);
      setNewTitle('');
      setNewText('');
    }
  }

  async function toggleTask(task: TaskItem) {
    if (task.id.startsWith('draft-')) return;
    const nextDone = !task.done;
    setTasks((prev) =>
      prev.map((item) => (item.id === task.id ? { ...item, done: nextDone } : item)),
    );

    await fetch('/api/students/guides/checklist', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: task.id, done: nextDone }),
    });
  }

  async function updateTask(taskId: string, title: string, text: string) {
    if (!title.trim() || !text.trim() || taskId.startsWith('draft-')) return;

    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, title, text } : t)));

    await fetch('/api/students/guides/checklist', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: taskId, title, text }),
    });
  }

  async function deleteTask(taskId: string) {
    setTasks((prev) => prev.filter((item) => item.id !== taskId));
    if (taskId.startsWith('draft-')) return;
    await fetch(`/api/students/guides/checklist?id=${taskId}`, { method: 'DELETE' });
  }

  async function resetProgress() {
    const completed = tasks.filter((t) => t.done && !t.id.startsWith('draft-'));
    setTasks((prev) => prev.map((task) => ({ ...task, done: false })));

    await Promise.all(
      completed.map((task) =>
        fetch('/api/students/guides/checklist', {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ id: task.id, done: false }),
        }),
      ),
    );
  }

  if (pathname !== '/students/guides') return null;

  if (status === 'loading' || isLoading) {
    return (
      <section className="mt-8 rounded-[32px] border border-main/25 bg-slate-900/45 p-5">
        Загрузка…
      </section>
    );
  }

  if (!isAuthed) {
    return (
      <section className="mt-8 rounded-[32px] border border-main/25 bg-gradient-to-br from-slate-900/55 via-slate-900/40 to-sky-950/55 p-6">
        <h2 className="text-2xl font-black text-main">Персональный чеклист студента</h2>
        <p className="mt-3 text-slate-100/90">
          Чтобы создавать свои карточки, редактировать, удалять и сохранять прогресс в базе данных,
          войдите в аккаунт.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-8 rounded-[32px] border border-main/25 bg-gradient-to-br from-slate-900/55 via-slate-900/40 to-sky-950/55 p-5 shadow-[0_24px_70px_-36px_rgba(250,204,21,0.28)] backdrop-blur-xl sm:p-6">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-main/35 bg-main/10 px-3 py-1 text-xs font-black text-main">
            <Target className="h-4 w-4" /> Мой прогресс на неделю
          </div>
          <h2 className="mt-4 text-2xl font-black text-main sm:text-3xl">
            Гибкий чеклист студента — сохранение в БД
          </h2>
          <p className="mt-2 text-sm leading-7 text-slate-100/90 sm:text-base">
            Добавляйте свои задачи, переименовывайте карточки и отмечайте выполнение — данные
            привязаны к вашему аккаунту.
          </p>
        </div>

        <div className="min-w-[220px] rounded-3xl border border-white/10 bg-white/5 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-3xl font-black text-main">{progressPercent}%</div>
              <div className="mt-1 text-sm text-slate-100/80">
                {completedCount} из {tasks.length} выполнено
              </div>
            </div>
            <Sparkles className="h-8 w-8 text-main" />
          </div>
          <button
            onClick={resetProgress}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-slate-100 transition hover:border-main/50 hover:text-main"
          >
            <RotateCcw className="h-4 w-4" /> Сбросить прогресс
          </button>
        </div>
      </div>

      <div className="mt-5 grid gap-3 rounded-3xl border border-white/10 bg-white/5 p-4">
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Заголовок карточки"
          className="rounded-2xl border border-white/15 bg-slate-900/60 px-4 py-3 text-sm text-white outline-none focus:border-main/60"
        />
        <textarea
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="Описание"
          className="min-h-[88px] rounded-2xl border border-white/15 bg-slate-900/60 px-4 py-3 text-sm text-white outline-none focus:border-main/60"
        />
        <button
          onClick={createTask}
          className="inline-flex w-fit items-center gap-2 rounded-2xl bg-main px-4 py-2 text-sm font-black text-sky-950"
        >
          <Plus className="h-4 w-4" /> Добавить карточку
        </button>
      </div>

      <div className="mt-6 grid auto-rows-fr gap-3 md:grid-cols-2 xl:grid-cols-3">
        {tasks.map((task) => (
          <article
            key={task.id}
            className={`flex h-full min-h-[160px] flex-col gap-3 rounded-3xl border p-4 ${task.done ? 'border-main/55 bg-main/15' : 'border-white/10 bg-white/5'}`}
          >
            <button
              onClick={() => toggleTask(task)}
              className="inline-flex items-center gap-2 text-left text-main"
            >
              <CheckCircle2 className="h-4 w-4" />{' '}
              {task.done ? 'Выполнено' : 'Отметить как выполнено'}
            </button>
            <input
              defaultValue={task.title}
              onBlur={(e) => updateTask(task.id, e.target.value, task.text)}
              className="rounded-xl border border-white/10 bg-slate-900/50 px-3 py-2 text-sm font-black text-main"
            />
            <textarea
              defaultValue={task.text}
              onBlur={(e) => updateTask(task.id, task.title, e.target.value)}
              className="min-h-[70px] rounded-xl border border-white/10 bg-slate-900/50 px-3 py-2 text-sm text-slate-100"
            />
            <button
              onClick={() => deleteTask(task.id)}
              className="mt-auto inline-flex w-fit items-center gap-2 rounded-xl border border-rose-300/40 px-3 py-2 text-xs font-semibold text-rose-200"
            >
              <Trash2 className="h-3.5 w-3.5" /> Удалить
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}
